import { lazy, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedLayout } from './components/ProtectedLayout';
import { LoginPage } from './pages/LoginPage';

/**
 * Cada página vira um chunk próprio: o carregamento inicial baixa apenas o
 * shell + a rota atual, em vez do console inteiro.
 */
const load = {
  dashboard: () => import('./pages/DashboardPage'),
  analytics: () => import('./pages/AnalyticsPage'),
  users: () => import('./pages/UsersPage'),
  userDetail: () => import('./pages/UserDetailPage'),
  payments: () => import('./pages/PaymentsPage'),
  subscriptions: () => import('./pages/SubscriptionsPage'),
  support: () => import('./pages/SupportPage'),
  licenses: () => import('./pages/LicensesPage'),
  coupons: () => import('./pages/CouponsPage'),
  marketing: () => import('./pages/MarketingPage'),
  cartRecovery: () => import('./pages/CartRecoveryPage'),
  affiliates: () => import('./pages/AffiliatesPage'),
  linktree: () => import('./pages/LinktreePage'),
  access: () => import('./pages/AccessPage'),
  ai: () => import('./pages/AiPage'),
  releases: () => import('./pages/ReleasesPage'),
};

const DashboardPage = lazy(() => load.dashboard().then((m) => ({ default: m.DashboardPage })));
const AnalyticsPage = lazy(() => load.analytics().then((m) => ({ default: m.AnalyticsPage })));
const UsersPage = lazy(() => load.users().then((m) => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => load.userDetail().then((m) => ({ default: m.UserDetailPage })));
const PaymentsPage = lazy(() => load.payments().then((m) => ({ default: m.PaymentsPage })));
const SubscriptionsPage = lazy(() => load.subscriptions().then((m) => ({ default: m.SubscriptionsPage })));
const SupportPage = lazy(() => load.support().then((m) => ({ default: m.SupportPage })));
const LicensesPage = lazy(() => load.licenses().then((m) => ({ default: m.LicensesPage })));
const CouponsPage = lazy(() => load.coupons().then((m) => ({ default: m.CouponsPage })));
const MarketingPage = lazy(() => load.marketing().then((m) => ({ default: m.MarketingPage })));
const CartRecoveryPage = lazy(() => load.cartRecovery().then((m) => ({ default: m.CartRecoveryPage })));
const AffiliatesPage = lazy(() => load.affiliates().then((m) => ({ default: m.AffiliatesPage })));
const LinktreePage = lazy(() => load.linktree().then((m) => ({ default: m.LinktreePage })));
const AccessPage = lazy(() => load.access().then((m) => ({ default: m.AccessPage })));
const AiPage = lazy(() => load.ai().then((m) => ({ default: m.AiPage })));
const ReleasesPage = lazy(() => load.releases().then((m) => ({ default: m.ReleasesPage })));

/**
 * Aquece os demais chunks quando a thread principal fica ociosa. Assim o
 * code-splitting não cobra pedágio na primeira navegação: quando o operador
 * clica no menu, o chunk já está em cache.
 */
function usePrefetchRoutes() {
  useEffect(() => {
    const idle: (cb: () => void) => number =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback ||
      ((cb) => window.setTimeout(cb, 1200));

    const handle = idle(() => {
      Object.values(load).forEach((importer) => {
        importer().catch(() => { /* prefetch é best-effort */ });
      });
    });
    return () => {
      const cancel = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (cancel) cancel(handle);
      else clearTimeout(handle);
    };
  }, []);
}

export default function App() {
  usePrefetchRoutes();

  // O Suspense fica dentro do Layout (em volta do Outlet), para que o shell —
  // sidebar, topbar e tab bar — permaneça na tela enquanto o chunk carrega.
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/:uid" element={<UserDetailPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/licenses" element={<LicensesPage />} />
        <Route path="/coupons" element={<CouponsPage />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/cart-recovery" element={<CartRecoveryPage />} />
        <Route path="/affiliates" element={<AffiliatesPage />} />
        <Route path="/linktree" element={<LinktreePage />} />
        <Route path="/access" element={<AccessPage />} />
        <Route path="/ai" element={<AiPage kind="ai" />} />
        <Route path="/support-ai" element={<AiPage kind="support-ai" />} />
        <Route path="/releases" element={<ReleasesPage />} />
      </Route>
    </Routes>
  );
}
